import { useAppSelector } from "@/store/hooks";
import { FormEvent, ReactNode, useEffect, useState } from "react";
import PriceSelector from "../PriceSelector";
import { Button, Col, Form, Input, InputGroup, InputGroupText, Label, Row, Table } from "reactstrap";
import Select from "react-select";
import styles from "./style.module.scss";
import { generateSelectOption, SelectOptionType } from "@utils/Utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faMagnifyingGlass, faPlus, faXmark } from "@fortawesome/free-solid-svg-icons";
import LoadingScreen from "@components/content/LoadingScreen";
import { ProductApi, ProductDataType } from "@/api/ProductsApi";
import { CategoryApi } from "@/api/CategoryApi";
import { calculateProductPrice } from "@utils/EDocument";
import { showAlert } from "@utils/Alert";
import { MEILI_SEARCH_ACTIVATE } from "@components/layout/ModelIndex";
import { ApiClient } from "@/api/ApiClient";
import { TAXES_PERCENTAGES_VALUES } from "@utils/Constants";
import { formatCant, roundCant } from "@utils/Cant";

type GeneralProductSearchProps = {
  onProductSelect: (product: ProductDataType, cant: number, price?: number) => void,
  productSelection?: boolean
  useProductCost?: boolean
  editablePrice?: boolean
  calculateTaxes?: boolean
};

const MAX_ITEMS_PER_PAGE = 300;

const GeneralProductSearch = (props: GeneralProductSearchProps) => {
  const { onProductSelect, productSelection = false, useProductCost = false, editablePrice = false, calculateTaxes = false } = props;
  const [products, setProducts] = useState<ProductDataType[]>([]);
  const currencySymbol = useAppSelector((state) => state.currency.currencySymbol);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [relativeValue, setRelativeValue] = useState<string>("");
  const [nextPageToken, setNextPageToken] = useState<string>("");
  const [inputValue, setInputValue] = useState<string>("");
  const [filterText, setFilterText] = useState<string | boolean>(false);
  const [hasMorePages, setHasMorePages] = useState(false);
  const [categoryOptions, setCategoryOptions] = useState<SelectOptionType[]>([]);
  const currentSelectOption = categoryOptions.find(o => o.value === relativeValue)
  const priceCode = useAppSelector((state) => state.eDocument.priceCode);
  const priceData = useAppSelector((state) => state.eDocument.priceData);
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [prices, setPrices] = useState<Record<string, number>>({});

  useEffect(() => {
    loadCategoriesOptions()
  }, [])

  const loadCategoriesOptions = async () => {
    const categories = await CategoryApi.optionsList({}, (item) => `${item.codigo}-${item.nombre}`);
    const options = generateSelectOption(categories)
    setCategoryOptions(options)
  }

  useEffect(() => {
    if (filterText !== false) {
      const run = async () => {
        const items = await fetchProducts();
        setProducts(items);
      }
      run()
    }
  }, [filterText, relativeValue]);

  const fetchProducts = async (token: string = ''): Promise<ProductDataType[]> => {
    setIsLoading(true)
    try {

      let nextToken, items;
      if (relativeValue) {
        const data = await ProductApi.fetchAllByCategory(relativeValue, generateFilterCriteria(`${filterText}`), token, MAX_ITEMS_PER_PAGE);
        items = data.items;
        nextToken = data.nextToken
      } else {
        if (MEILI_SEARCH_ACTIVATE) {
          return await meiliSearchProduct(1)
        } else {
          const data = await ProductApi.fetchAll(generateFilterCriteria(`${filterText}`), token, MAX_ITEMS_PER_PAGE, { noCache: true });
          items = data.items;
          nextToken = data.nextToken
        }
      }

      setHasMorePages(!!nextToken);
      setNextPageToken(nextToken || "");
      return items;

    } finally {
      setIsLoading(false)
    }
  }

  const meiliSearchProduct = async (pageNumber: number): Promise<ProductDataType[]> => {
    try {
      const result = await ApiClient.queries.searchGeneric({
        index: "products",
        q: `${filterText || ""}`,
        page: pageNumber,
        hitsPerPage: 300,
        sort: ["codigo:asc"]
      })
      const items = JSON.parse(`${result.data?.hits}`)

      return items;
    } finally {
      setIsLoading(false)
    }
  }

  const search = (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setFilterText(inputValue);
  }

  const handleQuantityChange = (id: string, value: string) => {
    setQuantities((prev) => ({
      ...prev,
      [id]: value
    }));
  };
  const handlePriceChange = (id: string, value: string) => {
    setPrices((prev) => ({
      ...prev,
      [id]: Number(value)
    }));
  };

  const generateFilterCriteria = (filterText: string) => {
    const productFilterObject = [
      { codigo: { contains: filterText } },
      { descripcion_fact: { contains: filterText } },
      { codigobarras: { eq: filterText } },
    ]
    return productFilterObject.length ? {
      or: productFilterObject
    } : []

  }

  const clearSearchForm = () => {
    setInputValue("");
    setFilterText("");
    setRelativeValue("");
  }

  const renderFilterForm = (): ReactNode | null => {
    return (
      <>
        <Form onSubmit={(e) => search(e)}>
          <Row className="row-cols-lg-auto g-1 align-items-center">
            <Col>
              <Label className="g-0 pe-3"
                for="general-search-criteria"
              >
                Filtro
              </Label>
            </Col>
            <Col lg="3">
              <Select
                name="categoryFilter"
                placeholder="Categoria"
                value={currentSelectOption || null}
                options={categoryOptions}
                onChange={(newValue) => { setRelativeValue(`${newValue?.value}`) }}
              />
            </Col>
            <Col lg="3">
              <Input
                id="general-search-criteria"
                name="filter"
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
            </Col>
            <Col>
              <Button className="me-1" color="primary" type="submit">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
              </Button>
              <Button color="danger" type="button" onClick={() => clearSearchForm()}>
                <FontAwesomeIcon icon={faXmark} />
              </Button>
            </Col>
          </Row>
        </Form>
        <hr />
      </>
    )
  }

  const selectProduct = (product: ProductDataType, currentQuantity: string, currentPrice?: number) => {
    const quantity = roundCant(Number(currentQuantity || "1"));
    if (isNaN(quantity) || quantity <= 0) {
      showAlert("danger", "La cantidad no es valida")
      return;
    }

    onProductSelect(product, quantity, currentPrice);
  }

  const onCantKeyDown = (product: ProductDataType, cant: string, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();

      selectProduct(product, cant)
    }
  }

  const getProductPrice = (product: ProductDataType) => {
    let price = useProductCost ? product.costo : calculateProductPrice(product, priceCode || priceData, calculateTaxes)

    if (editablePrice) {
      return price
    } else {
      return `${currencySymbol || ""} ${price}`
    }
  }


  const renderProduct = (): ReactNode[] => {
    return products.map(p => {
      const productPrice = getProductPrice(p)
      const currentQuantity = quantities[`${p.id}`] || "1"
      const currenPrice = Number(prices[`${p.id}`] || productPrice)
      return (
        <tr>
          <td>
            {p.codigo}
          </td>
          <td className="col-5">
            {p.descripcion_fact}
          </td>
          {!useProductCost && (
            <td className="col-1 text-center">
              {formatCant(p.existencias)}
            </td>
          )}
          <td className="col-2 text-center">
            {editablePrice && (<InputGroup>
              <Input
                type="number"
                defaultValue="1"
                value={currenPrice}
                onChange={(e) => handlePriceChange(`${p.id}`, e.target.value)}
              />
            </InputGroup>
            )}
            {!editablePrice && productPrice}
          </td>
          <td className="col-2">
            {productSelection ? (
              <Button type="button" onClick={() => { selectProduct(p, "1", currenPrice) }} color="primary"><FontAwesomeIcon className="me-2" icon={faCheck} />  Seleccionar</Button>
            ) :
              (<InputGroup>
                <Input
                  type="number"
                  step="0.001"
                  min="0"
                  defaultValue="1"
                  value={currentQuantity}
                  onChange={(e) => handleQuantityChange(`${p.id}`, e.target.value)}
                  onKeyDown={(e) => onCantKeyDown(p, currentQuantity, e)}
                />
                <InputGroupText className="p-0">
                  <Button type="button" className={styles.addProductBtn} onClick={() => { selectProduct(p, currentQuantity) }} color="primary"><FontAwesomeIcon icon={faPlus} /> Agregar</Button>
                </InputGroupText>
              </InputGroup>
              )}
          </td>
        </tr>
      )
    })
  }

  const loadMoreItems = async () => {
    const items = await fetchProducts(nextPageToken);
    setProducts([
      ...products,
      ...items]
    );
  }

  return (
    <>
      <Row>
        <Col>
          {renderFilterForm()}
        </Col>
        <Col xs="1">
          <PriceSelector />
        </Col>
      </Row>
      <Table className={styles.productTable} size="sm">
        <thead>
          <tr>
            <th>
              Codigo
            </th>
            <th>
              Descripcion
            </th>
            {!useProductCost && (
              <th className="text-center">
                Existencias
              </th>
            )}
            <th className="text-center">
              {useProductCost ? "Costo" : "Precio"}
            </th>
            <th>
              {productSelection ? "" : "Cantidad a Agregar"}
            </th>
          </tr>
        </thead>
        <tbody>
          {renderProduct()}
        </tbody>
      </Table>
      {hasMorePages && (
        <div className={styles.loadMoreButton}>
          <Button type="button" color="primary" onClick={loadMoreItems}>Cargar Mas</Button>
        </div>
      )}

      <LoadingScreen isLoading={isLoading} />
    </>
  )
}

export default GeneralProductSearch;